/**
 * File: TableLayout.js
 * Origin: isotect
 * Description: TableLayout component to render a table layout with controls, table, and pagination.
 *
 * Version: v1.1.0
 * Created: 2026-04-30
 * Last Updated: 2026-07-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useEffect, useState } from "react";

// Local project files
import { filterTableRows, searchTableRows } from "../../utils/tableUtilities";
import PaginationControls from "../layout/PaginationControls";
import RowControls from "./RowControls";
import Table from "../ui/Table";
import TableControls from "./TableControls";

/**
 * Renders a table layout with controls, table, and pagination.
 *
 * @component
 *
 * @param {Object} props - Component props
 * @param {Object} props.schema - Table configuration schema
 * @param {Array<Object>} props.tableRows - Table rows to display
 * @param {(tableRows: Array<Object>) => void} props.setTableRows - Callback to change the table rows
 * @param {(state: string) => void} props.setTableView - Callback to change the table view
 *
 * @returns {JSX.Element} Table layout with controls, table, and pagination
 */
function TableLayout({ schema, tableRows, setTableRows, setTableView }) {
    const [viewRows, setViewRows] = useState([]);

    const [query, setQuery] = useState("");
    const [filters, setFilters] = useState({});

    const [allRowsSelected, setAllRowsSelected] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        setViewRows(tableRows.map((row) => ({ ...row })));
    }, [tableRows]);

    const handleTableViewChange = (state) => {
        setQuery("");
        setFilters({});
        setAllRowsSelected(false);
        setSelectedRows([]);
        setCurrentPage(1);
        setTableView(state);
    };

    const handleTableQuery = (query) => {
        setQuery(query);
        setAllRowsSelected(false);
        setSelectedRows([]);
        setCurrentPage(1);

        const filteredRows = filterTableRows(tableRows, filters);
        const rows = searchTableRows(filteredRows, query);

        setViewRows(rows);
    };

    const handleTableFilters = (filters) => {
        setQuery("");
        setFilters(filters);
        setAllRowsSelected(false);
        setSelectedRows([]);
        setCurrentPage(1);

        const rows = filterTableRows(tableRows, filters);

        setViewRows(rows);
    };

    return (
        <div className="table-layout">
            <TableControls
                tableId={schema.id}
                schema={schema.tableControls}
                setView={handleTableViewChange}
                query={query}
                setQuery={handleTableQuery}
                filters={filters}
                setFilters={handleTableFilters}
            />
            <div className="table-layout__table-container">
                <div className="table-layout__table">
                    <Table
                        id={schema.id}
                        headers={schema.headers}
                        rows={viewRows}
                        appearance={{ transposed: false }}
                        components={{
                            caption: false,
                            header: true,
                            footer: false,
                        }}
                        features={{ selectable: true }}
                        state={{
                            allRowsSelected,
                            currentPage,
                            rowsPerPage,
                            selectedRows,
                        }}
                        callbacks={{
                            setAllRowsSelected,
                            setSelectedRows,
                        }}
                    />
                </div>
            </div>
            <RowControls
                rows={viewRows}
                setTableRows={setTableRows}
                primaryKey={schema.primaryKey}
                controlButtons={schema.rowControls}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                setAllRowsSelected={setAllRowsSelected}
            />
            <PaginationControls
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                totalTableRows={viewRows.length}
            />
        </div>
    );
}

export default TableLayout;
